import * as Sentry from '@sentry/vue';

import SkConfig from '@/common/config';
import { logError } from '@/common/helpers/error-handling';
import { prefStorage } from '@samknows/utils';
import {
  getUserAccessibles,
  getUserGrants,
  getUserHashedId,
  getUserPreferredLocale
} from './userSession';
import ZeusService from '@/common/services/Zeus';
import { ActiveViewData, VIEW_TYPE } from '@/common/helpers/view-types';
import { hasPermissions } from '@/common/services/account/userPermissions';
import { Accessibles, GRANT_SECTION } from '@/common/services/account/types';

/**
 *
 * @returns
 */
export function setSavedUserPreferencesFromServer(): Promise<unknown> {
  const userId = getUserHashedId();

  return ZeusService.get(`users/${userId}/preferences`)
    .then((data) => {
      if (data && data.data) {
        prefStorage._setObject(data.data);
      }
    })
    .catch(logError);
}

/**
 * Save grants in the pref storage
 * Grants are a list of all the permissions the user has
 */
export function setGrants(): void {
  const grants = getUserGrants();
  prefStorage.set('grants', grants);
}

/**
 * Save accessible in the pref storage
 * Accessibles contains:
 * - a list of whiteboxes the user is linked
 * - user flags which are not part of the session
 */
export function setAccessibles(): void {
  const accessibles = getUserAccessibles();
  prefStorage.set('accessibles', accessibles);
}

/**
 * Check if the current selected whitebox has trigger testing capacity
 * Check once and save in pref storage for future use in the app
 *
 * @param unitId {number} Id of the unit to check
 */
export function saveIsUnitTriggerTestingEnabled(unitId: number): Promise<void> {
  return new Promise((resolve) => {
    if (!hasPermissions(GRANT_SECTION.AGENT, 'trigger_testing')) {
      prefStorage.set(`instant-testable-${unitId}`, false).then(resolve);
      return;
    }

    const userId = getUserHashedId();
    return ZeusService.get(`users/${userId}/accessible-units/${unitId}`)
      .then((data) => {
        const testable = data.data.is_tt_compatible;
        prefStorage.set(`instant-testable-${unitId}`, testable).then(resolve);
      })
      .catch(() => {
        prefStorage.set(`instant-testable-${unitId}`, false).then(resolve);
      });
  });
}

/**
 * Pref storage is an object cached in the local storage throughout the user session for easy access
 * Some of the properties are saved on the server so it can retrieved between sessions
 *
 * Pref storage is required for the app to properly work and needs to setup in a specific order:
 * - Retrieved saved items from the server and set pref storage with this object as default
 * - Save grants and accessibles from user session
 * - Set user locale
 * - Set active view depending on accessibles
 *
 * This function handle adding all properties required upon app load
 */
export async function setupPreferences(): Promise<void> {
  await setSavedUserPreferencesFromServer();

  setGrants();
  setAccessibles();

  // Setup locale preferences
  const preferredLocale = getUserPreferredLocale();
  const isValidLocale = !!SkConfig.locales.find(
    ({ code }) => code === preferredLocale
  );

  if (isValidLocale) {
    prefStorage.set('locale', preferredLocale);
    Sentry.setTag('locale', preferredLocale);
  }

  const accessibles: Accessibles = prefStorage.get('accessibles');

  let type = VIEW_TYPE.SIGNUP;
  let activeViewData: ActiveViewData = {};

  if (accessibles?.units?.length) {
    activeViewData = accessibles.units[0];
    type = VIEW_TYPE.CONSUMER;
  }

  prefStorage.set('active-view', { type, data: activeViewData });

  await saveIsUnitTriggerTestingEnabled(activeViewData.unitId);
}

/**
 * Setup hook that will enables us to save some updates to our external prefStorage
 * Needed to retrieve data in between sessions
 * Needs ot happen on app load
 */
export function setSavePreferencesOnServerHook(): void {
  const userId = getUserHashedId();
  try {
    prefStorage.setServerHook((serverStorage) => {
      return ZeusService.update(`users/${userId}/preferences`, {
        samknows_one_preferences: serverStorage
      });
    });
  } catch (error) {}
}

/**
 * Check whether the pref storage has been setup
 *
 * 3 main items are required for the app to work properly
 * - grants
 * - accessibles
 * - active view
 *
 * @returns {boolean}
 */
export function isPrefStorageSetup(): boolean {
  const hasGrants = !!prefStorage.get('grants');
  const hasAccessible = !!prefStorage.get('accessibles');
  const hasActiveView = !!prefStorage.get('active-view');

  return hasGrants && hasAccessible && hasActiveView;
}
